import * as THREE from 'three'
import { Putanja } from './evropa'

export interface PuteviResult {
  group: THREE.Group
  dispose(): void
}

/** Visina mostovske ploče iznad nivoa mora. */
const VISINA_MOSTA = 7

/**
 * Putevi su trake nalepljene na teren, mostovi su ravne ploče na stubovima.
 * Sve ide u dve spojene geometrije — mreža ima trideset linija, ne bi imalo
 * smisla praviti trideset objekata.
 */
export function buildRoads(putanje: Putanja[], mapSize: number): PuteviResult {
  const skala = mapSize / 1600
  const sirinaPuta = 6.8 * skala
  const sirinaMosta = 7.6 * skala
  const podizanje = 1.8 * skala

  const putPoz: number[] = []
  const putNor: number[] = []
  const putIdx: number[] = []

  const konPoz: number[] = []
  const konNor: number[] = []
  const konIdx: number[] = []

  const stubovi: THREE.Matrix4[] = []
  const pomocna = new THREE.Vector3()
  const normala = new THREE.Vector3(0, 1, 0)

  for (const put of putanje) {
    const naMostu = new Uint8Array(put.tacke.length)
    for (const [a, b] of put.mostovi) {
      for (let i = a; i <= b; i++) naMostu[i] = 1
    }

    // Visina ploče mosta: ravna između oslonaca, da most ne prati talase vode.
    const visine = put.tacke.map((t, i) => (naMostu[i] ? VISINA_MOSTA * skala : t.y + podizanje))
    for (const [a, b] of put.mostovi) {
      const ya = put.tacke[a].y + podizanje
      const yb = put.tacke[b].y + podizanje
      const vrh = Math.max(VISINA_MOSTA * skala, ya, yb)
      for (let i = a; i <= b; i++) {
        // Blage rampe na krajevima, ravno u sredini.
        const t = (i - a) / Math.max(1, b - a)
        const rampa = Math.min(1, Math.min(t, 1 - t) * 6)
        visine[i] = ya + (yb - ya) * t + (vrh - (ya + (yb - ya) * t)) * rampa
      }
    }

    // Svaka tačka daje par temena u obe trake — tako spoj puta i mosta
    // nema rupu, a segment se crta iz one trake kojoj pripada.
    const putBaza = putPoz.length / 3
    const konBaza = konPoz.length / 3

    for (let i = 0; i < put.tacke.length; i++) {
      const t = put.tacke[i]
      const pre = put.tacke[Math.max(0, i - 1)]
      const posle = put.tacke[Math.min(put.tacke.length - 1, i + 1)]
      pomocna.set(posle.x - pre.x, 0, posle.z - pre.z)
      if (pomocna.lengthSq() < 1e-9) pomocna.set(1, 0, 0)
      pomocna.normalize()
      // Popreko na pravac, u ravni mape.
      const bx = -pomocna.z
      const bz = pomocna.x
      const y = visine[i]

      const wp = sirinaPuta * 0.5
      // Put nikad ne tone ispod vode: tamo gde bi, tu ionako stoji most.
      const yp = Math.max(y, 0.6 * skala)
      putPoz.push(t.x - bx * wp, yp, t.z - bz * wp)
      putPoz.push(t.x + bx * wp, yp, t.z + bz * wp)
      putNor.push(normala.x, normala.y, normala.z, normala.x, normala.y, normala.z)

      const wm = sirinaMosta * 0.5
      konPoz.push(t.x - bx * wm, y, t.z - bz * wm)
      konPoz.push(t.x + bx * wm, y, t.z + bz * wm)
      konNor.push(normala.x, normala.y, normala.z, normala.x, normala.y, normala.z)
    }

    for (let i = 0; i < put.tacke.length - 1; i++) {
      const most = naMostu[i] === 1 || naMostu[i + 1] === 1
      const baza = most ? konBaza : putBaza
      const idx = most ? konIdx : putIdx
      const a = baza + i * 2
      const b = baza + (i + 1) * 2
      // Redosled je bitan: normala mora da gleda naviše, inače su svi
      // trouglovi zadnja lica i backface culling ih pobriše.
      idx.push(a, a + 1, b, a + 1, b + 1, b)
    }

    // Stubovi ispod mostova.
    for (const [a, b] of put.mostovi) {
      const korak = Math.max(2, Math.round((b - a) / 12))
      for (let i = a + korak; i < b; i += korak) {
        const t = put.tacke[i]
        const visinaStuba = visine[i] - Math.min(t.y, -1)
        const m = new THREE.Matrix4()
        m.compose(
          new THREE.Vector3(t.x, visine[i] - visinaStuba / 2, t.z),
          new THREE.Quaternion(),
          new THREE.Vector3(1.6 * skala, visinaStuba, 1.6 * skala),
        )
        stubovi.push(m)
      }
    }
  }

  const group = new THREE.Group()
  group.name = 'putevi'
  const zaBrisanje: { dispose(): void }[] = []

  const putMat = new THREE.MeshStandardMaterial({
    color: '#e3d3ac',
    roughness: 0.85,
    metalness: 0,
    polygonOffset: true,
    polygonOffsetFactor: -3,
    polygonOffsetUnits: -3,
  })
  const konMat = new THREE.MeshStandardMaterial({ color: '#c9c3ba', roughness: 0.7 })
  const stubMat = new THREE.MeshStandardMaterial({ color: '#8f8a83', roughness: 0.8 })
  zaBrisanje.push(putMat, konMat, stubMat)

  const put = napraviMesh(putPoz, putNor, putIdx, putMat)
  if (put) {
    // Trake namerno ne primaju senku: leže par jedinica iznad terena, pa im
    // teren ispod njih upada u shadow mapu i ceo put ispadne crn.
    put.receiveShadow = false
    group.add(put)
    zaBrisanje.push(put.geometry)
  }

  const most = napraviMesh(konPoz, konNor, konIdx, konMat)
  if (most) {
    most.castShadow = true
    most.receiveShadow = false
    group.add(most)
    zaBrisanje.push(most.geometry)
  }

  if (stubovi.length) {
    const geo = new THREE.BoxGeometry(1, 1, 1)
    const inst = new THREE.InstancedMesh(geo, stubMat, stubovi.length)
    for (let i = 0; i < stubovi.length; i++) inst.setMatrixAt(i, stubovi[i])
    inst.instanceMatrix.needsUpdate = true
    inst.castShadow = true
    group.add(inst)
    zaBrisanje.push(geo, inst)
  }

  return {
    group,
    dispose() {
      for (const x of zaBrisanje) x.dispose()
    },
  }
}

function napraviMesh(
  poz: number[],
  nor: number[],
  idx: number[],
  mat: THREE.Material,
): THREE.Mesh | null {
  if (!idx.length) return null
  const geo = new THREE.BufferGeometry()
  geo.setAttribute('position', new THREE.Float32BufferAttribute(poz, 3))
  geo.setAttribute('normal', new THREE.Float32BufferAttribute(nor, 3))
  geo.setIndex(idx)
  geo.computeBoundingSphere()
  return new THREE.Mesh(geo, mat)
}
